import { computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { PlatformType } from '@indutrust/shared-model';
import {
  getScreenSizeCategory,
  isSmallScreenWidth,
} from '@indutrust/shared-model';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import type { Subscription } from 'rxjs';
import { debounceTime, fromEvent } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export type PlatformState = {
  platformType: PlatformType;
  screenWidth: number;
  screenHeight: number;
};

const getPlatformType = (): PlatformType => {
  // Simple user agent detection that can be improved for more accuracy.
  const win = globalThis.window as Window | undefined;
  if (win === undefined || typeof navigator === 'undefined') {
    return 'desktop';
  }
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/.test(ua)) {
    return 'mobile';
  }
  if (/ipad|tablet|kindle|playbook/.test(ua)) {
    return 'tablet';
  }
  return 'desktop';
};

const getScreenSize = (): { width: number; height: number } => {
  const win = globalThis.window as Window | undefined;
  if (win === undefined) {
    return { width: 0, height: 0 };
  }
  return {
    width: win.innerWidth,
    height: win.innerHeight,
  };
};

const initialType = getPlatformType();
const initialScreen = getScreenSize();

const initialState: PlatformState = {
  platformType: initialType,
  screenWidth: initialScreen.width,
  screenHeight: initialScreen.height,
};

/**
 * Signal store for platform and viewport characteristics (desktop/mobile/tablet and screen size).
 *
 * Exposes derived helpers like isSmallScreen and screenSizeCategory for responsive behavior.
 *
 * @example
 * import { inject } from '@angular/core';
 * import { PlatformStore } from '@indutrust/shared-data';
 *
 * export class ExampleComponent {
 *   readonly platform = inject(PlatformStore);
 *
 *   isCompact() {
 *     return this.platform.isSmallScreen();
 *   }
 * }
 */
export const PlatformStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ screenWidth, platformType }) => ({
    isTouchDevice: computed(
      () => platformType() === 'mobile' || platformType() === 'tablet'
    ),
    screenSizeCategory: computed(() => getScreenSizeCategory(screenWidth())),
  })),
  withComputed(({ screenWidth }) => ({
    isSmallScreen: computed(() => isSmallScreenWidth(screenWidth())),
  })),
  withMethods((store) => ({
    /**
     * Updates the platform type based on a provided user agent string (or current navigator).
     *
     * @param userAgent - Optional user agent to parse instead of using the browser's.
     * @example
     * platformStore.updatePlatform('Mozilla/5.0 (iPhone; CPU iPhone OS 16_0 like Mac OS X)');
     */
    updatePlatform(userAgent?: string) {
      let type: PlatformType | null = null;
      if (userAgent === undefined) {
        type = getPlatformType();
      } else {
        const ua = userAgent.toLowerCase();
        if (
          /mobile|android|iphone|ipod|blackberry|iemobile|opera mini/.test(ua)
        ) {
          type = 'mobile';
        } else if (/ipad|tablet|kindle|playbook/.test(ua)) {
          type = 'tablet';
        } else {
          type = 'desktop';
        }
      }
      patchState(store, {
        platformType: type,
      });
    },
    /**
     * Updates the current viewport dimensions.
     *
     * @param width - Width in pixels.
     * @param height - Height in pixels.
     * @example
     * platformStore.updateScreenSize(1024, 768);
     */
    updateScreenSize(width: number, height: number) {
      patchState(store, {
        screenWidth: width,
        screenHeight: height,
      });
    },
  })),
  withHooks((store) => {
    let resizeSubscription: Subscription | undefined = undefined;
    const destroyRef = inject(DestroyRef);

    return {
      /**
       * Lifecycle hook that subscribes to window resize events and updates dimensions.
       *
       * @example
       * // Automatically called on store initialization
       */
      onInit() {
        const win = globalThis.window as Window | undefined;
        if (win !== undefined) {
          resizeSubscription = fromEvent(win, 'resize')
            .pipe(
              debounceTime(500),
              startWith(null),
              map(() => ({
                width: win.innerWidth,
                height: win.innerHeight,
              })),
              takeUntilDestroyed(destroyRef)
            )
            .subscribe(({ width, height }) => {
              store.updateScreenSize(width, height);
            });
        }
      },
      /**
       * Lifecycle hook that cleans up the resize subscription.
       */
      onDestroy() {
        resizeSubscription?.unsubscribe();
      },
    };
  })
);
